import { memo, useEffect, useRef } from "react";

interface BlogPostContentProps {
  html: string;
  chartData?: string | null;
}

const PROSE_CLASS =
  "blog-prose prose prose-neutral dark:prose-invert max-w-none prose-headings:font-display prose-headings:tracking-tight prose-headings:scroll-mt-24 prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4 prose-h3:text-lg prose-h3:mt-8 prose-h3:mb-3 prose-p:leading-[1.8] prose-p:text-foreground/85 prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-strong:text-foreground prose-li:text-foreground/85 prose-li:leading-[1.8] prose-img:rounded-xl prose-pre:bg-muted/50 prose-pre:border prose-pre:border-border/30 prose-code:text-primary prose-code:before:content-[''] prose-code:after:content-[''] prose-blockquote:border-l-primary/50 prose-blockquote:bg-muted/20 prose-blockquote:py-1 prose-blockquote:px-4 prose-blockquote:rounded-r-lg prose-hr:border-border/30";

/**
 * Renders pre-compiled, server-sanitized blog HTML and hydrates any
 * `[data-chart]` blocks into ECharts on the client.
 *
 * Memoized on `html`: the parent re-renders on every scroll (reading-progress
 * bar), and without memo React re-applies dangerouslySetInnerHTML and wipes the
 * ECharts <canvas> elements out from under us. Re-rendering only when the HTML
 * actually changes keeps the charts alive.
 */
function BlogPostContentImpl({ html }: BlogPostContentProps) {
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = contentRef.current;
    if (!root) return;
    const chartElements = root.querySelectorAll<HTMLElement>("[data-chart]");
    if (chartElements.length === 0) return;

    let cancelled = false;
    type Disposable = { dispose: () => void; resize: () => void };
    const charts: Disposable[] = [];

    (async () => {
      try {
        const echarts = await import("echarts/core");
        const { BarChart, LineChart, PieChart } = await import(
          "echarts/charts"
        );
        const {
          TitleComponent,
          TooltipComponent,
          GridComponent,
          LegendComponent,
        } = await import("echarts/components");
        const { CanvasRenderer } = await import("echarts/renderers");

        echarts.use([
          BarChart,
          LineChart,
          PieChart,
          TitleComponent,
          TooltipComponent,
          GridComponent,
          LegendComponent,
          CanvasRenderer,
        ]);

        if (cancelled) return;

        for (const el of chartElements) {
          const encoded = el.getAttribute("data-chart");
          if (!encoded) continue;
          // Idempotent: never double-init the same node.
          if (echarts.getInstanceByDom(el)) continue;
          try {
            // UTF-8-safe base64 decode: atob() yields a Latin-1 string, which
            // corrupts multi-byte characters (em dashes, accents). Decode the
            // bytes through TextDecoder so any Unicode in chart text survives.
            const bytes = Uint8Array.from(atob(encoded), (ch) =>
              ch.charCodeAt(0),
            );
            const option = JSON.parse(new TextDecoder().decode(bytes));
            const chart = echarts.init(el);
            chart.setOption(option);
            charts.push(chart);
          } catch (err) {
            console.warn("[blog-chart] failed to render a chart", err);
          }
        }
      } catch (err) {
        console.warn("[blog-chart] failed to load ECharts", err);
      }
    })();

    const onResize = () => {
      for (const c of charts) c.resize();
    };
    window.addEventListener("resize", onResize);

    return () => {
      cancelled = true;
      window.removeEventListener("resize", onResize);
      for (const c of charts) c.dispose();
    };
  }, [html]);

  return (
    <div
      ref={contentRef}
      className={PROSE_CLASS}
      // Content is pre-compiled and sanitized server-side before storage.
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

export const BlogPostContent = memo(BlogPostContentImpl);
